// @flow

import './VideoControls.css';

import * as React from 'react';
import _, { cloneWith } from 'lodash';
import classNames from 'classnames';
import InputSlider from '../common/InputSlider';
import VideoController from './VideoController';
import PlaybackTimer from '../tabs/PlaybackTimer';
import ClippingToolbar from '../common/ClippingToolbar';
import Action from '../common/Action';
import { isTouchDevice, getScreenSize, ScreenSize, getInnerWidth, BreakpointMinWidth } from '../util/ScreenUtil';
import AnnotationUtil from '../queries/annotations/AnnotationUtil';
import { AnnotationThread } from '../autogenerated/ZoomBridge';
import Dropdown from '../common/Dropdown';
import Button from '../common/Button';
import { playbackRateOptions } from '../util/VideoUtil';
import Center from '../common/layout/Center';
import TimelineComments from './TimelineComments';
import { getFilteredComments } from '../store/StoreUtil';
import { isMobile } from '../util/ScreenUtil';
import type { ClipConfig } from '../hoc/Clip';
import Spinner from '../common/Spinner';
import t from '../i18n/I18n';
import ViewUtil from '../ViewUtil';
import { ThemeSetup } from '../themes/ThemeSetup';

const debug = require('../debug/debug')('wc:video:VideoControls');

type Props = {
  activeAudioTrackIndex: number,
  audioTrackOptions: Object[],
  bufferProgress: number,
  canWriteComments: boolean,
  clipConfig: ClipConfig,
  comments: Object[],
  controller: VideoController,
  enableComments: boolean,
  isAudio: boolean,
  isFullscreen: boolean,
  mediaLoadFailed: boolean,
  onAudioTrackChange: Function,
  onCommentButtonClick: Function,
  onCommentTimestampMakerClick: Function,
  onGotoNextComment: Function,
  onGotoPrevComment: Function,
  onMuted: Function,
  onQualityChange: Function,
  onSeekBack: Function,
  onSeekChange: Function,
  onSeekChangeComplete: Function,
  onSeekForward: Function,
  onSpeedChange: Function,
  onSubtitleChange: Function,
  onVolume: Function,
  playPause: Function,
  quality: ?Object,
  qualityOptions: Object[],
  renderAudioComment: Function,
  showComments: boolean,
  showMetadataTimeline: boolean,
  subtitleOptions: Object[],
  subtitle: ?Object,
  toggleShowComments: Function,
  videoFrameRate: number,
  renderDownloadButton: Function
};

type State = {
  useMobileSettingsLayout: boolean,
  showSettings: boolean,
  showDuration: boolean,
  showControls: boolean
};

export default class VideoControls extends React.Component<Props, State> {
  _node: ?HTMLDivElement;
  _progressBar: ?HTMLDivElement;
  _mouseState: MouseState = new MouseState();
  _fullscreenMouseTimer: any;
  _didJustChangeShowControlState: boolean;
  _showVideoControl: boolean;
  _isCollectionTabActive: boolean;
  static defaultProps = {
    renderDownloadButton: _.noop
  };

  state = {
    useMobileSettingsLayout: isMobile(),
    showSettings: false,
    showDuration: true,
    showControls: true,
  };

  componentDidMount() {
    if (this.props.isFullscreen) {
      this.addFullscreenListeners();
    }
    this._isCollectionTabActive = ViewUtil.isCollectionTabActive();
  }

  componentWillReceiveProps(nextProps: Props) {
    if (!this.props.isFullscreen && nextProps.isFullscreen) {
      this.addFullscreenListeners();
    } else if (this.props.isFullscreen && !nextProps.isFullscreen) {
      this.removeFullscreenListeners();
    }
  }

  componentDidUpdate() {
    if (!this.state.useMobileSettingsLayout && this.doesControlBarHaveOverlaps()) {
      this.setState({ useMobileSettingsLayout: true });
    }
  }

  componentWillUnmount() {
    if (this.props.isFullscreen) {
      this.removeFullscreenListeners();
    }
  }

  addFullscreenListeners = () => {
    if (isTouchDevice()) {
      document.addEventListener('touchend', this.onTouch);
    } else {
      document.addEventListener('mousemove', this.onMouseMove);
    }
  };

  removeFullscreenListeners = () => {
    if (isTouchDevice()) {
      document.removeEventListener('touchend', this.onTouch);
    } else {
      document.removeEventListener('mousemove', this.onMouseMove);
    }

    clearTimeout(this._fullscreenMouseTimer);
  };

  onTouch = () => {
    setTimeout(() => {
      var dropDownElement = document.getElementsByName('Dropdown_List');
      if (dropDownElement.length) {
        this.showControls(true);
        return;
      }

      if (this._showVideoControl) {
        this.showControls(true);
        return;
      }
      var commentElement = document.getElementsByName('AddCommentForm');
      if (commentElement.length) {
        return;
      }

      this.setState({ showControls: !this.state.showControls });
    }, 200);
  };

  onMouseMove = (e: MouseEvent) => {
    const pos = {
      x: e.clientX,
      y: e.clientY
    };

    var dropDownElement = document.getElementsByName('Dropdown_List');
    if (dropDownElement.length) {
      this.showControls(true);
      return;
    }

    this._mouseState.determineState(pos);
    if (this._mouseState.isCloseToControls && !this.state.showControls) {
      this.showControls(true);
    } else if (!this._mouseState.isCloseToControls && this.state.showControls) {
      this.showControls(false);
    }

    clearTimeout(this._fullscreenMouseTimer);
    this._fullscreenMouseTimer = setTimeout(this.checkMousePosition, 2000);
  };

  checkMousePosition = () => {
    debug('[checkMousePosition()]');
    if (this._mouseState.lastPos.y >= window.innerHeight - 50) {
      debug('[checkMousePosition()] still hovering over controls. Try again in 2s.');
      clearTimeout(this._fullscreenMouseTimer);
      this._fullscreenMouseTimer = setTimeout(this.checkMousePosition, 2000);
      return;
    }

    if (!this.state.showControls) {
      debug('[checkMousePosition()] !showControls. return.');
      return;
    }

    this.showControls(false);
  };

  showControls = (showControls: boolean) => {
    if (this._didJustChangeShowControlState) {
      debug('[showControls()] _didJustChangeShowControlState. return.');
      return;
    }

    this._didJustChangeShowControlState = true;
    setTimeout(() => (this._didJustChangeShowControlState = false), 500);

    debug('[showControls()] setting showControls state: ', showControls);
    this.setState({ showControls });
  };

  doesControlBarHaveOverlaps = () => {
    if (!this._node) {
      return false;
    }

    const rightNode: Element = this._node.querySelector('.video-settings');
    const centerNode = rightNode.previousElementSibling;

    const rightRect = rightNode.getBoundingClientRect();
    const centerRect = centerNode.getBoundingClientRect();

    const diff = rightRect.left - centerRect.right;
    return diff < 20;
  };

  printUniqueUserNames = (comments: AnnotationThread[]) => {
    return _.uniq(comments.map(AnnotationUtil.getCreatorOfThread));
  };

  getTimelineCommentPosition = (timestamp: number) => {
    const duration = this.props.controller.duration * 1000;
    return { left: `${(timestamp / duration) * 100}%` };
  };

  onShowSettingsClick = () => {
    this._showVideoControl = !this._showVideoControl;
    this.setState({ showSettings: !this.state.showSettings }, () => {
      this.props.changeVideoHeight();
    });
  };

  onRef = (node: ?HTMLDivElement) => {
    this._node = node;
    if (node && node.clientWidth < 550) {
      this.setState({ showDuration: false });
    }
  };

  renderVideoSettings = (isMobileContent: boolean = false) => {
    const {
      activeAudioTrackIndex,
      audioTrackOptions,
      controller,
      enableComments,
      isAudio,
      onAudioTrackChange,
      onQualityChange,
      onSpeedChange,
      onSubtitleChange,
      quality,
      qualityOptions,
      showComments,
      subtitle,
      subtitleOptions,
      toggleShowComments,
      renderDownloadButton
    } = this.props;
    const { useMobileSettingsLayout, showSettings } = this.state;
    if (useMobileSettingsLayout && !isMobileContent && getInnerWidth() < BreakpointMinWidth.LG) {
      return (
        <div style={{flexBasis:'25%', textAlign: 'right'}}>
          <Button
            className="mobile-toggle"
            icon={showSettings ? 'images/arrow_up.svg' : 'images/arrow_down.svg'}
            onClick={this.onShowSettingsClick}
          />
        </div>
       
      );
    }

    const { playbackRate } = controller;
    const audioTrack = audioTrackOptions.length ? audioTrackOptions[activeAudioTrackIndex].value : null;
    const showCommentsLabel = getScreenSize() === ScreenSize.XS ? 'Comments' : 'Show Comments';

    return (
      <div className={`flex-row align-center video-settings ${classNames({ mobile: isMobileContent })}`}>
        {enableComments && (
          <Button
            title="Show comments while playing."
            type="LINK"
            icon={showComments ? ThemeSetup.getImageURL('checkmark_red.svg') : 'images/radio_unchecked.svg'}
            text={showCommentsLabel}
            onClick={toggleShowComments}
          />
        ) }

        {subtitleOptions.length ? (
          <Dropdown theme="PLAIN" anchor='RIGHT' value={subtitle} options={subtitleOptions} onChange={onSubtitleChange} />
        ) : null}
        {audioTrackOptions.length ? (
          <Dropdown theme="PLAIN" anchor='RIGHT' value={audioTrack} options={audioTrackOptions} onChange={onAudioTrackChange} />
        ) : null}
        {isAudio ? null : (
          <Dropdown theme="PLAIN" anchor='RIGHT' value={quality} options={qualityOptions} onChange={onQualityChange} />
        )}
        <Dropdown theme="PLAIN" anchor='RIGHT' value={playbackRate} options={playbackRateOptions} onChange={onSpeedChange} />
        {renderDownloadButton()}
      </div>
    );
  };

  renderCommentMarkers = () => {
    const { controller, enableComments, showComments, comments, onCommentTimestampMakerClick } = this.props;
    if (!(enableComments && showComments)) {
      return null;
    }

    const filteredComments = getFilteredComments(comments).filter((c) => !!AnnotationUtil.getTemporalLocation(c));

    const filteredAndSortedComments = _.sortBy(filteredComments, (c: AnnotationThread) =>
      AnnotationUtil.getVideOffsetInMilliseconds(c)
    );

    return (
      <TimelineComments
        comments={filteredAndSortedComments}
        duration={controller.duration}
        onCommentClick={onCommentTimestampMakerClick}
      />
    );
  };

  renderMobileSettings = () => {
    const { useMobileSettingsLayout, showSettings } = this.state;
    if (!(useMobileSettingsLayout && showSettings)) {
      return null;
    }

    return this.renderVideoSettings(true);
  };

  render() {
    const {
      bufferProgress,
      canWriteComments,
      clipConfig,
      controller,
      enableComments,
      isFullscreen,
      mediaLoadFailed,
      onCommentButtonClick,
      onGotoNextComment,
      onGotoPrevComment,
      onMuted,
      onSeekBack,
      onSeekChange,
      onSeekChangeComplete,
      onSeekForward,
      onVolume,
      playPause,
      renderAudioComment,
      showMetadataTimeline,
      videoFrameRate,
      callingFrom,
      showComments
    } = this.props;

    const { clipMode } = clipConfig;
    const { currentTime, duration, volume, muted, playing } = controller;
    const { showDuration, showControls } = this.state;
    const isCollectionTabActive = this._isCollectionTabActive;
    let showPlaybackTimer = true;

    if (isCollectionTabActive && !callingFrom) {
      showPlaybackTimer = true;
    } else if (showMetadataTimeline && !isFullscreen) {
      showPlaybackTimer = false;
    }

    return (
      <div
        name="VideoControls"
        className={classNames({
          show: showControls,
          fullscreen: isFullscreen
        })}
        ref={this.onRef}>
        {clipMode ? (
          <ClippingToolbar
            clipConfig={clipConfig}
            duration={duration}
            width={this._node ? this._node.getBoundingClientRect().width : 0}
          />
        ) : null}
        {
          isCollectionTabActive ? <div id="seekBar" className="seekbar" ref={(node) => (this._progressBar = node)}>
            {this.renderCommentMarkers()}
            {renderAudioComment('#seekBar')}
            <InputSlider
              ref="seekBar"
              className="slider"
              axis="x"
              x={currentTime}
              x2={bufferProgress}
              x2color="var(--input-progress-bar)"
              xmin={0}
              xmax={duration}
              xstep={0.01}
              onChange={onSeekChange}
              onDragEnd={onSeekChangeComplete}
            />
          </div> : null
        }
        
        <div className={classNames('control-bar', { 'no-duration': !showDuration })}>
          {showPlaybackTimer && !mediaLoadFailed ? (
            <PlaybackTimer
              ref="playbackTimer"
              progress={currentTime}
              duration={duration}
              videoFrameRate={videoFrameRate}
              controller={controller}
            />
          ) : (
            <div className={getScreenSize() < ScreenSize.MD ? "no-timer": null} style={{flexBasis: '25%'}}/>
          )}
          <div className={getScreenSize() === ScreenSize.LG && isCollectionTabActive ? 'avoid-center' : 'center-controls'}>
            {duration ? (
              <div className="flex-row align-center controls">
                <Action custom predicates={[() => enableComments && canWriteComments]}>
                  <Button
                    icon={ThemeSetup.getImageURL('add_timeline_comment_red.svg')}
                    title={t('ADD_COMMENT')}
                    onClick={() => onCommentButtonClick(controller.currentTime * 1000)}
                  />
                </Action>
                { enableComments && showComments && (
                  <Button
                    icon={ThemeSetup.getImageURL('icon_go_to_left.svg')}
                    title={t('GO_TO_PREV_COMMENT')}
                    onClick={onGotoPrevComment}
                  />
                ) }
                <Button
                  icon={ThemeSetup.getImageURL('icon_seek_back.svg')}
                  title={t('STEP_BACKWARD')}
                  onClick={onSeekBack}
                />
                {!playing ? (
                  <Button icon={ThemeSetup.getImageURL('icon_play.svg')} onClick={playPause} title={t('PLAY')} />
                ) : (
                  <Button icon={ThemeSetup.getImageURL('icon_pause.svg')} onClick={playPause} title={t('PAUSE')} />
                )}
                <Button
                  icon={ThemeSetup.getImageURL('icon_seek_fwd.svg')}
                  onClick={onSeekForward}
                  title={t('STEP_FORWARD')}
                />
                { enableComments && showComments && (
                  <Button
                    icon={ThemeSetup.getImageURL('icon_go_to_right.svg')}
                    title={t('GO_TO_NEXT_COMMENT')}
                    onClick={onGotoNextComment}
                  />
                ) }
                {isTouchDevice() ? null : (
                  <Button
                    icon={
                      muted
                        ? ThemeSetup.getImageURL('speaker_icon_muted.svg')
                        : ThemeSetup.getImageURL('speaker_icon.svg')
                    }
                    title={t('VOLUME')}
                    onClick={onMuted}
                  />
                )}
                {isTouchDevice() ? null : (
                  <InputSlider
                    className="volume"
                    axis="x"
                    x={muted ? 0 : volume}
                    xmin={0}
                    xmax={1}
                    xstep={0.01}
                    onChange={onVolume}
                  />
                )}
              </div>
            ) : mediaLoadFailed ? (
              <div className="error-message">{t('VIDEO_PLAYER.MEDIA_PLAYER_FAILED')}</div>
            ) : (
              <Spinner small />
            )}
          </div>
          {this.renderVideoSettings()}
        </div>
        {getInnerWidth() < BreakpointMinWidth.LG ? this.renderMobileSettings() : null}
      </div>
    );
  }
}

type Position = {
  x: number,
  y: number
};

class MouseState {
  lastPos: Position = { x: 0, y: 0 };
  isMovingDown: boolean;
  isCloseToControls: boolean;
  determineState(pos: Position) {
    this.isMovingDown = pos.y > this.lastPos.y;
    this.isCloseToControls = pos.y > window.innerHeight - 200;
    this.lastPos = pos;
  }
}
